import moment from "moment-timezone";
import {useFieldArray, useFormContext} from "react-hook-form";
import {FiPlus} from "react-icons/fi";

import Button from "@/components/button/Button.tsx";
import {Dropdown} from "@/components/dropdown/Dropdown.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {MonthlyScheduleRow} from "@/components/form/maintenance/MonthlyScheduleRow.tsx";
import {OneOffScheduleRow} from "@/components/form/maintenance/OneOffScheduleRow.tsx";
import {ScheduleEntriesFormValues, ScheduleEntryType} from "@/components/form/maintenance/types.ts";
import {WeeklyScheduleRow} from "@/components/form/maintenance/WeeklyScheduleRow.tsx";
import {Icon} from "@/components/icon/Icon";
import {ServiceField} from "@/lib/constants/service.ts";
import {timezoneAbbr} from "@/lib/format.tsx";

import {ScheduleEntryContainer} from "./ScheduleEntryContainer";

interface ScheduleEntriesProps {
  timezone: string;
  className?: string;
}

export const ScheduleEntries = ({timezone, className}: ScheduleEntriesProps) => {
  const name = ServiceField.SCHEDULE_ENTRIES;
  const {
    control,
    clearErrors,
    formState: {errors},
  } = useFormContext<ScheduleEntriesFormValues>();
  const {fields, append, remove} = useFieldArray({
    control,
    name,
  });
  const entriesError = errors[name]?.message;
  const scheduleEntryTypeCount = Object.fromEntries(
    Object.keys(ScheduleEntryType).map((key) => [key, fields.filter((field) => field.type === key).length])
  );

  return (
    <div className={className}>
      {entriesError && <FormErrorAlerts className="mb-3" errors={[entriesError]} />}
      <ScheduleEntryContainer className="mb-3" title={`One Off Schedule (${timezoneAbbr(timezone)})`}>
        {scheduleEntryTypeCount[ScheduleEntryType.ONCE] > 0 && (
          <div className="container p-0 mx-0">
            {fields.map((f, index) => {
              if (f.type !== ScheduleEntryType.ONCE) {
                return null;
              }
              return <OneOffScheduleRow key={`oneoff-${f.id}`} name={name} id={f.id} index={index} remove={remove} />;
            })}
          </div>
        )}
        <Button
          variant="secondary"
          onClick={() => {
            clearErrors([name]);
            append({
              type: ScheduleEntryType.ONCE,
              weekdays: [],
              from_time: "00:00",
              to_time: "00:00",
              once_start_date: moment().format(),
              once_end_date: moment().add(2, "hour").format(),
              monthday_from: 0,
              monthday_to: 0,
            });
          }}
        >
          <Icon icon={FiPlus} />
          Add Schedule
        </Button>
      </ScheduleEntryContainer>
      <ScheduleEntryContainer className="mb-3" title={`Weekly Schedule (${timezoneAbbr(timezone)})`}>
        {scheduleEntryTypeCount[ScheduleEntryType.WEEKLY] > 0 && (
          <div className="container p-0 mx-0">
            {fields.map((f, index) => {
              if (f.type !== ScheduleEntryType.WEEKLY) {
                return null;
              }
              return <WeeklyScheduleRow key={`weekly-${f.id}`} id={f.id} name={name} index={index} remove={remove} />;
            })}
          </div>
        )}
        <Button
          variant="secondary"
          onClick={() => {
            clearErrors([name]);
            append({
              type: ScheduleEntryType.WEEKLY,
              weekdays: [],
              from_time: "00:00:00",
              to_time: "00:00:00",
              once_start_date: moment().format(),
              once_end_date: moment().add(2, "hour").format(),
              monthday_from: 0,
              monthday_to: 0,
            });
          }}
        >
          <Icon icon={FiPlus} />
          Add Schedule
        </Button>
      </ScheduleEntryContainer>
      <ScheduleEntryContainer title={`Monthly Schedule (${timezoneAbbr(timezone)})`}>
        {scheduleEntryTypeCount[ScheduleEntryType.MONTHLY] > 0 && (
          <div className="container p-0 mx-0">
            {fields.map((f, index) => {
              if (f.type !== ScheduleEntryType.MONTHLY) {
                return null;
              }
              return <MonthlyScheduleRow key={`monthly-${f.id}`} name={name} id={f.id} index={index} remove={remove} />;
            })}
          </div>
        )}
        <Dropdown>
          <Dropdown.Toggle variant="secondary">
            <Icon icon={FiPlus} />
            Add Schedule
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item
              onClick={() => {
                append({
                  type: ScheduleEntryType.MONTHLY,
                  weekdays: [],
                  from_time: "00:00",
                  to_time: "24:00",
                  once_start_date: "",
                  once_end_date: "",
                  monthday_from: 1,
                  monthday_to: 31,
                });
              }}
            >
              Add Date Range Schedule
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => {
                append({
                  type: ScheduleEntryType.MONTHLY,
                  weekdays: [],
                  from_time: "00:00",
                  to_time: "24:00",
                  once_start_date: "",
                  once_end_date: "",
                  monthday: 1,
                });
              }}
            >
              Add Single Day Schedule
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </ScheduleEntryContainer>
    </div>
  );
};
